import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { HubOverlay } from '../ui/dom/HubOverlay';

/**
 * MenuScene — thin Phaser shell that:
 *   1. Paints an ambient OLED-obsidian background with floating particles
 *      (kept in Phaser so they layer BEHIND the DOM overlay naturally).
 *   2. Hosts the DOM HubOverlay for all interactive chrome (hub dashboard).
 *   3. Bridges DOM overlay events back to scene.start() calls.
 *
 * Why DOM for the menu: native frosted glass via backdrop-filter, flex
 * layout, spring easing via cubic-bezier, and cheap text rendering vs.
 * Phaser's per-frame canvas text. See docs/review.md revised Part 3 §1.
 */
export class MenuScene extends Phaser.Scene {
  private particles: Phaser.GameObjects.Arc[] = [];
  private overlay: HubOverlay | null = null;

  constructor() {
    super({ key: 'Menu' });
  }

  create(): void {
    this.particles = [];

    // OLED-obsidian background with a subtle radial vignette.
    this.cameras.main.setBackgroundColor('#050507');

    this.drawCyanVignette();
    this.createFloatingParticles();

    // DOM overlay — everything interactive lives here.
    this.overlay = new HubOverlay();
    this.overlay.on('play',     () => this.transitionTo('Game'));
    this.overlay.on('codex',    () => this.transitionTo('Codex'));
    this.overlay.on('shop',     () => this.transitionTo('Shop'));
    this.overlay.on('settings', () => this.transitionTo('Settings'));
    this.overlay.on('toast',    ({ message }) => this.showToast(message));
    this.overlay.open();

    // Tear down the overlay on scene shutdown so it never stacks on top of itself.
    this.events.once('shutdown', () => {
      this.overlay?.close();
      this.overlay = null;
    });
    this.events.once('destroy', () => {
      this.overlay?.close();
      this.overlay = null;
    });
  }

  update(time: number, delta: number): void {
    // Drift the ambient particles upward like embers.
    for (const particle of this.particles) {
      const data = particle.getData('velocity') as { vx: number; vy: number };
      particle.x += data.vx * (delta / 1000);
      particle.y += data.vy * (delta / 1000);

      if (particle.x < -20) particle.x = GAME_WIDTH + 20;
      if (particle.x > GAME_WIDTH + 20) particle.x = -20;
      if (particle.y < -20) particle.y = GAME_HEIGHT + 20;
      if (particle.y > GAME_HEIGHT + 20) particle.y = -20;

      const scale = 0.8 + 0.2 * Math.sin(time / 1000 + particle.x);
      particle.setScale(scale);
    }
  }

  /** Soft cyan radial glow painted behind the DOM overlay for depth. */
  private drawCyanVignette(): void {
    const gfx = this.add.graphics();
    gfx.setDepth(0);
    // Two concentric filled circles to fake a radial gradient
    gfx.fillStyle(0x0A0D18, 0.6);
    gfx.fillCircle(GAME_WIDTH / 2, GAME_HEIGHT * 0.42, GAME_WIDTH * 0.9);
    gfx.fillStyle(0x00F0FF, 0.05);
    gfx.fillCircle(GAME_WIDTH / 2, GAME_HEIGHT * 0.42, GAME_WIDTH * 0.55);
  }

  /**
   * Decorative floating particles — single muted cyan color, consistent drift.
   * VISUAL-IDENTITY §6.4: not random colors, not random directions.
   */
  private createFloatingParticles(): void {
    const COLOR = 0x00F0FF;
    const OPACITY = 0.12;
    const DRIFT_VX = 4;
    const DRIFT_VY = -6;
    const PARTICLE_RADIUS = 2;

    for (let i = 0; i < 18; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;

      const particle = this.add.circle(x, y, PARTICLE_RADIUS, COLOR, OPACITY);
      particle.setDepth(1);
      particle.setData('velocity', {
        vx: DRIFT_VX + (Math.random() - 0.5) * 1.5,
        vy: DRIFT_VY + (Math.random() - 0.5) * 1.5,
      });

      this.particles.push(particle);
    }
  }

  /** Close the DOM overlay before switching scenes so it fades out properly. */
  private transitionTo(key: string): void {
    this.overlay?.close();
    // Give the overlay ~220ms to start fading before the scene swap yanks focus.
    this.time.delayedCall(200, () => this.scene.start(key));
  }

  /**
   * Ephemeral toast for hub nav buttons that don't have a destination yet
   * (Battle Pass, Stats, Achievements). Rendered as a transient DOM element
   * so it floats above the hub overlay without needing its own scene wiring.
   */
  private showToast(message: string): void {
    const host = document.body;
    const toast = document.createElement('div');
    toast.className = 'hub-toast';
    toast.textContent = message;
    host.appendChild(toast);
    // Reflow → add visible class for spring-in
    void toast.offsetWidth;
    toast.classList.add('is-visible');
    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 320);
    }, 1600);
  }
}
